import { useEffect, useState, useMemo } from "react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { registerTrFont } from "@/lib/pdfFont";
import { fmtDate, todayISO } from "@/lib/format";
import api from "@/lib/api";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import { PageHeader, Empty, StatCard, Segmented } from "@/components/Shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Combobox } from "@/components/Combobox";
import { contactOptions } from "@/lib/contacts";
import { DatePicker } from "@/components/DatePicker";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Package, Boxes, ClipboardList, ArrowRightLeft, Activity, Search, BookOpen, Building2, FileDown, FileText, History, Plus, Pencil, Trash2, ArrowRight, CalendarDays, CalendarRange, Users, ListChecks } from "lucide-react";
import { toast } from "sonner";
import LoginEvents from "@/components/activities/LoginEvents";
import { ShieldCheck } from "lucide-react";

const AUDIT_ACTION = {
  create: { label: "Oluşturuldu", icon: Plus, color: "bg-[#16A34A]/15 text-[#16A34A]", stripe: "bg-[#16A34A]" },
  update: { label: "Güncellendi", icon: Pencil, color: "bg-secondary/15 text-secondary", stripe: "bg-secondary" },
  delete: { label: "Silindi", icon: Trash2, color: "bg-destructive/15 text-destructive", stripe: "bg-destructive" },
};
const auditVal = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Evet" : "Hayır";
  if (typeof v === "number") return Number.isInteger(v) ? String(v) : v.toFixed(2);
  return String(v);
};

const meta = {
  urun_ekle: { icon: Package, color: "bg-[#16A34A]/15 text-[#16A34A]", stripe: "bg-[#16A34A]", label: "Ürün Eklendi" },
  urun_duzenle: { icon: Package, color: "bg-secondary/15 text-secondary", stripe: "bg-secondary", label: "Ürün Düzenlendi" },
  urun_sil: { icon: Package, color: "bg-destructive/15 text-destructive", stripe: "bg-destructive", label: "Ürün Silindi" },
  stok_giris: { icon: Boxes, color: "bg-[#16A34A]/15 text-[#16A34A]", stripe: "bg-[#16A34A]", label: "Stok Girişi" },
  stok_cikis: { icon: Boxes, color: "bg-destructive/15 text-destructive", stripe: "bg-destructive", label: "Stok Çıkışı" },
  sayim: { icon: ArrowRightLeft, color: "bg-secondary/15 text-secondary", stripe: "bg-secondary", label: "Sayım" },
  siparis_talep: { icon: ClipboardList, color: "bg-primary/15 text-primary", stripe: "bg-primary", label: "Sipariş Talebi" },
  cari_defter_ekle: { icon: BookOpen, color: "bg-[#16A34A]/15 text-[#16A34A]", stripe: "bg-[#16A34A]", label: "Cari Defter · Ekleme" },
  cari_defter_duzenle: { icon: BookOpen, color: "bg-secondary/15 text-secondary", stripe: "bg-secondary", label: "Cari Defter · Düzenleme" },
  cari_defter_sil: { icon: BookOpen, color: "bg-destructive/15 text-destructive", stripe: "bg-destructive", label: "Cari Defter · Silme" },
  cari_sil: { icon: Building2, color: "bg-destructive/15 text-destructive", stripe: "bg-destructive", label: "Cari Silindi" },
  cari_aktar: { icon: BookOpen, color: "bg-primary/15 text-primary", stripe: "bg-primary", label: "Excel'den Cari Aktarımı" },
};
const FALLBACK = { icon: Activity, color: "bg-muted text-muted-foreground", stripe: "bg-slate-400" };
const labelOf = (a) => (meta[a.action] || {}).label || a.action;
const contactOf = (a) => (a.meta && a.meta.contact) || "";
const initials = (n) => String(n || "?").trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toLocaleUpperCase("tr");
const timeOf = (iso) => { try { return new Date(iso).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };
const dayKey = (iso) => { try { return new Date(iso).toLocaleDateString("tr-TR", { day: "2-digit", month: "long", year: "numeric", weekday: "long" }); } catch { return iso; } };

export default function Activities() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [contactSel, setContactSel] = useState("");
  const [contacts, setContacts] = useState([]);
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [view, setView] = useState(() => (new URLSearchParams(window.location.search).get("tab") === "girisler" ? "logins" : "activities"));
  const [audit, setAudit] = useState([]);

  const loadItems = () => {
    const url = `/activities?limit=500${contactSel ? `&contact=${encodeURIComponent(contactSel)}` : ""}`;
    api.get(url).then((r) => setItems(r.data)).catch(() => {});
  };
  const loadAudit = () => { api.get("/audit-logs?limit=400").then((r) => setAudit(r.data)).catch(() => {}); };
  useEffect(() => { loadItems(); /* eslint-disable-next-line */ }, [contactSel]);
  useEffect(() => { if (view === "history") loadAudit(); }, [view]);
  useEffect(() => { api.get("/contacts").then((r) => setContacts(r.data)).catch(() => {}); }, []);
  useAutoRefresh(() => { if (view === "history") loadAudit(); else loadItems(); });

  const filtered = useMemo(() => items.filter((a) => {
    const ds = String(a.created_at || "").slice(0, 10);
    if (dateRange.start && ds && ds < dateRange.start) return false;
    if (dateRange.end && ds && ds > dateRange.end) return false;
    return (type === "all" || a.action === type || (type === "cari_defter" && String(a.action || "").startsWith("cari_defter"))) &&
      (String(a.description || "").toLowerCase().includes(q.toLowerCase()) || String(a.user_name || "").toLowerCase().includes(q.toLowerCase()));
  }), [items, q, type, dateRange]);

  const fullTime = (iso) => { try { return new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

  const periodLabel = () => (dateRange.start || dateRange.end)
    ? `${dateRange.start ? fmtDate(dateRange.start) : "Başlangıç"} - ${dateRange.end ? fmtDate(dateRange.end) : fmtDate(todayISO())}`
    : "Tüm Zamanlar";

  const today = todayISO();
  const weekAgo = new Date(Date.now() - 6 * 86400000).toISOString().slice(0, 10);
  const kpis = useMemo(() => {
    const src = view === "history" ? audit : items;
    const day = (x) => String(x.created_at || "").slice(0, 10);
    const byUser = {};
    src.forEach((x) => { const u = x.user_name || "—"; byUser[u] = (byUser[u] || 0) + 1; });
    const top = Object.entries(byUser).sort((a, b) => b[1] - a[1])[0];
    return { today: src.filter((x) => day(x) === today).length, week: src.filter((x) => day(x) >= weekAgo).length, total: src.length, topUser: top ? top[0] : "—", topCount: top ? top[1] : 0 };
  }, [items, audit, view, today, weekAgo]);

  const groups = useMemo(() => {
    const g = {};
    filtered.forEach((a) => { (g[dayKey(a.created_at)] = g[dayKey(a.created_at)] || []).push(a); });
    return g;
  }, [filtered]);

  const auditFiltered = useMemo(() => {
    const s = q.toLocaleLowerCase("tr");
    return audit.filter((l) => {
      const ds = String(l.created_at || "").slice(0, 10);
      if (dateRange.start && ds && ds < dateRange.start) return false;
      if (dateRange.end && ds && ds > dateRange.end) return false;
      if (!s) return true;
      return `${l.entity_name || ""} ${l.user_name || ""} ${l.entity_type_label || ""}`.toLocaleLowerCase("tr").includes(s);
    });
  }, [audit, q, dateRange]);
  const auditGroups = useMemo(() => {
    const g = {};
    auditFiltered.forEach((l) => { (g[dayKey(l.created_at)] = g[dayKey(l.created_at)] || []).push(l); });
    return g;
  }, [auditFiltered]);

  const exportExcel = () => {
    if (!filtered.length) return toast.error("Dışa aktarılacak aktivite yok");
    const rows = filtered.map((a) => ({ "Tarih": fullTime(a.created_at), "İşlem": labelOf(a), "Cari": contactOf(a), "Açıklama": a.description, "Kullanıcı": a.user_name }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = [{ wch: 22 }, { wch: 22 }, { wch: 26 }, { wch: 55 }, { wch: 20 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Aktiviteler");
    XLSX.writeFile(wb, `aktiviteler-${todayISO()}.xlsx`);
    toast.success(`${rows.length} aktivite Excel'e aktarıldı`);
  };

  const exportPdf = async () => {
    if (!filtered.length) return toast.error("Dışa aktarılacak aktivite yok");
    try {
      const pdf = new jsPDF("p", "mm", "a4");
      const FONT = await registerTrFont(pdf);
      const pageW = pdf.internal.pageSize.getWidth();
      const M = 12;
      const contentW = pageW - M * 2;
      const NAVY = [11, 15, 25];
      const ORANGE = [234, 88, 32];
      const drawHeader = () => {
        pdf.setFillColor(...ORANGE); pdf.roundedRect(M, 12, 3.5, 13, 1, 1, "F");
        pdf.setFont(FONT, "bold"); pdf.setFontSize(17); pdf.setTextColor(20, 20, 20);
        pdf.text("GÜMÜŞALAN ELEKTRİK", M + 7, 19);
        pdf.setFont(FONT, "normal"); pdf.setFontSize(8.5); pdf.setTextColor(150, 150, 150);
        pdf.text("Aktivite Kaydı", M + 7, 24);
        pdf.setFont(FONT, "bold"); pdf.setFontSize(12); pdf.setTextColor(...ORANGE);
        pdf.text("AKTİVİTELER", pageW - M, 18, { align: "right" });
        pdf.setFont(FONT, "normal"); pdf.setFontSize(8.5); pdf.setTextColor(150, 150, 150);
        pdf.text(`Dönem: ${periodLabel()}`, pageW - M, 23.5, { align: "right" });
        if (contactSel) pdf.text(`Cari: ${contactSel}`, pageW - M, 28, { align: "right" });
        pdf.setDrawColor(235, 235, 235); pdf.setLineWidth(0.4); pdf.line(M, 31, pageW - M, 31);
      };
      const body = filtered.map((a) => [fullTime(a.created_at), labelOf(a), contactOf(a) || "—", a.description, a.user_name]);
      autoTable(pdf, {
        head: [["Tarih", "İşlem", "Cari", "Açıklama", "Kullanıcı"]],
        body, startY: 35, margin: { left: M, right: M, top: 35 },
        styles: { font: FONT, fontSize: 8, cellPadding: 1.7, textColor: [45, 45, 45], lineColor: [235, 235, 235], lineWidth: 0.1, overflow: "linebreak", valign: "middle" },
        headStyles: { font: FONT, fontStyle: "bold", fillColor: NAVY, textColor: [255, 255, 255], fontSize: 8 },
        alternateRowStyles: { fillColor: [250, 250, 250] },
        columnStyles: { 0: { cellWidth: 34 }, 1: { cellWidth: 28 }, 2: { cellWidth: 32 }, 3: { cellWidth: contentW - 34 - 28 - 32 - 24 }, 4: { cellWidth: 24 } },
        didDrawPage: () => drawHeader(),
      });
      pdf.setFont(FONT, "normal"); pdf.setFontSize(7.5); pdf.setTextColor(180, 180, 180);
      pdf.text(`Toplam ${filtered.length} kayıt · ${fmtDate(todayISO())}`, pageW / 2, pdf.internal.pageSize.getHeight() - 8, { align: "center" });
      pdf.save(`aktiviteler-${todayISO()}.pdf`);
      toast.success(`${filtered.length} aktivite PDF'e aktarıldı`);
    } catch (e) { console.error(e); toast.error("PDF oluşturulamadı"); }
  };

  const isToday = dateRange.start === today && dateRange.end === today;
  const isWeek = dateRange.start === weekAgo && dateRange.end === today;
  const dateBar = (prefix) => (
    <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-2 bg-card border rounded-xl p-3" data-testid={`${prefix}-date-bar`}>
      <div className="flex-1">
        <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Başlangıç</Label>
        <DatePicker value={dateRange.start} onChange={(v) => setDateRange({ ...dateRange, start: v })} placeholder="Başlangıç" className="h-9" data-testid={`${prefix}-date-start`} />
      </div>
      <div className="flex-1">
        <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Bitiş</Label>
        <DatePicker value={dateRange.end} onChange={(v) => setDateRange({ ...dateRange, end: v })} placeholder="Bugün" className="h-9" data-testid={`${prefix}-date-end`} />
      </div>
      {(dateRange.start || dateRange.end) && <Button variant="ghost" size="sm" onClick={() => setDateRange({ start: "", end: "" })} data-testid={`${prefix}-date-clear`}>Temizle</Button>}
      <div className="text-xs text-muted-foreground sm:ml-auto self-center whitespace-nowrap tabular-nums" data-testid={`${prefix}-result-count`}>
        <b className="text-foreground">{prefix === "act" ? filtered.length : auditFiltered.length}</b> {prefix === "act" ? "kayıt" : "değişiklik"} · {periodLabel()}
      </div>
    </div>
  );

  return (
    <div className="space-y-6 stagger-in" data-testid="activities-page">
      <PageHeader title="Aktiviteler & Kayıt Geçmişi" badge="Canlı Akış" subtitle="Kullanıcı işlemleri ve kayıtların alan bazlı değişiklik geçmişi — kim, ne zaman, neyi değiştirdi">
        {view === "activities" && <>
          <Button variant="outline" onClick={exportExcel} data-testid="act-export-excel"><FileDown className="h-4 w-4 mr-2 text-emerald-600" />Excel</Button>
          <Button variant="outline" onClick={exportPdf} data-testid="act-export-pdf"><FileText className="h-4 w-4 mr-2 text-red-600" />PDF</Button>
        </>}
      </PageHeader>

      {view !== "logins" && <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="act-kpis">
        <StatCard label="Bugün" value={kpis.today} icon={CalendarDays} tone="primary" sub="işlem" testid="act-kpi-today" onClick={() => setDateRange(isToday ? { start: "", end: "" } : { start: today, end: today })} active={isToday} />
        <StatCard label="Son 7 Gün" value={kpis.week} icon={CalendarRange} tone="blue" sub="işlem" testid="act-kpi-week" onClick={() => setDateRange(isWeek ? { start: "", end: "" } : { start: weekAgo, end: today })} active={isWeek} />
        <StatCard label={view === "history" ? "Toplam Değişiklik" : "Toplam Aktivite"} value={kpis.total} icon={ListChecks} sub="yüklenen kayıt" testid="act-kpi-total" />
        <StatCard label="En Aktif Kullanıcı" value={<span className="text-lg sm:text-xl truncate block">{kpis.topUser}</span>} icon={Users} tone="success" sub={`${kpis.topCount} işlem`} testid="act-kpi-top-user" />
      </div>}

      <div data-testid="act-view-toggle">
        <Segmented value={view} onChange={setView} testid="act-view" options={[["activities", <span key="a" className="inline-flex items-center gap-1.5"><Activity className="h-4 w-4" />Aktiviteler</span>], ["history", <span key="h" className="inline-flex items-center gap-1.5"><History className="h-4 w-4" />Kayıt Geçmişi</span>], ["logins", <span key="l" className="inline-flex items-center gap-1.5" data-testid="act-view-logins"><ShieldCheck className="h-4 w-4" />Girişler</span>]]} />
      </div>

      {view === "logins" && <LoginEvents />}

      {view === "activities" && (<>
      <div className="flex flex-col lg:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Açıklama veya kullanıcı ara..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="act-search" />
        </div>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="w-full lg:w-52" data-testid="act-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm İşlemler</SelectItem>
            <SelectItem value="cari_defter">Cari Defter</SelectItem>
            <SelectItem value="urun_ekle">Ürün Eklendi</SelectItem>
            <SelectItem value="urun_duzenle">Ürün Düzenlendi</SelectItem>
            <SelectItem value="urun_sil">Ürün Silindi</SelectItem>
            <SelectItem value="stok_giris">Stok Girişi</SelectItem>
            <SelectItem value="stok_cikis">Stok Çıkışı</SelectItem>
            <SelectItem value="sayim">Sayım</SelectItem>
            <SelectItem value="siparis_talep">Sipariş Talebi</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2 w-full lg:w-64">
          <div className="flex-1 min-w-0">
            <Combobox options={contactOptions(contacts)} value={contactSel} onChange={setContactSel} placeholder="Cariye göre süz..." searchPlaceholder="Cari ara..." emptyText="Cari bulunamadı" data-testid="act-contact-filter" />
          </div>
          {contactSel && <Button variant="ghost" size="sm" onClick={() => setContactSel("")} data-testid="act-contact-clear">Temizle</Button>}
        </div>
      </div>

      {dateBar("act")}

      {filtered.length === 0 ? (
        <Empty title="Aktivite yok" desc={items.length ? "Filtreyi veya aramayı değiştirin." : "Kullanıcı işlemleri burada listelenecek."} />
      ) : (
        <div className="space-y-7">
          {Object.entries(groups).map(([date, list]) => (
            <div key={date}>
              <div className="flex items-center gap-3 mb-3">
                <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground font-head">{date}</span>
                <span className="text-[10px] tabular-nums rounded-full bg-muted px-2 py-0.5 text-muted-foreground">{list.length}</span>
                <span className="flex-1 h-px bg-border" />
              </div>
              <div className="relative border-l-2 border-border ml-4 space-y-2.5">
                {list.map((a) => {
                  const m = meta[a.action] || { ...FALLBACK, label: a.action };
                  const Icon = m.icon;
                  const cname = contactOf(a);
                  return (
                    <div key={a.id} className="relative pl-8" data-testid={`activity-${a.id}`}>
                      <div className={`absolute -left-[15px] top-3 h-7 w-7 rounded-full flex items-center justify-center ${m.color} ring-4 ring-background`}>
                        <Icon className="h-3.5 w-3.5" />
                      </div>
                      <div className="relative bg-card border rounded-xl pl-4 pr-4 py-3 overflow-hidden transition-all hover:shadow-md hover:-translate-y-px">
                        <span className={`absolute left-0 top-0 bottom-0 w-1 ${m.stripe}`} />
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="font-head font-bold text-sm">{m.label}</div>
                            <div className="text-sm text-muted-foreground mt-0.5 break-words">{a.description}</div>
                          </div>
                          <span className="font-mono text-xs text-muted-foreground whitespace-nowrap tabular-nums pt-0.5" title={fullTime(a.created_at)}>{timeOf(a.created_at)}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-foreground"><span className="h-5 w-5 rounded-full bg-primary/10 text-primary text-[9px] font-black flex items-center justify-center">{initials(a.user_name)}</span>{a.user_name}</span>
                          {cname && <span className="inline-flex items-center gap-1 text-[11px] font-medium bg-secondary/10 text-secondary rounded-full px-2 py-0.5" data-testid={`activity-contact-${a.id}`}><Building2 className="h-3 w-3" />{cname}</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
      </>)}

      {view === "history" && (
        <>
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Kayıt adı, kullanıcı veya tür ara..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="audit-search" />
          </div>
          {dateBar("audit")}

          {auditFiltered.length === 0 ? (
            <Empty title="Kayıt geçmişi yok" desc="Ürün, teklif, fatura ve cari değişiklikleri burada listelenecek." />
          ) : (
            <div className="space-y-7" data-testid="audit-list">
              {Object.entries(auditGroups).map(([date, logs]) => (
                <div key={date}>
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground font-head">{date}</span>
                    <span className="text-[10px] tabular-nums rounded-full bg-muted px-2 py-0.5 text-muted-foreground">{logs.length}</span>
                    <span className="flex-1 h-px bg-border" />
                  </div>
                  <div className="relative border-l-2 border-border ml-4 space-y-2.5">
                    {logs.map((l) => {
                      const a = AUDIT_ACTION[l.action] || AUDIT_ACTION.update;
                      const Icon = a.icon;
                      return (
                        <div key={l.id} className="relative pl-8" data-testid={`audit-${l.id}`}>
                          <div className={`absolute -left-[15px] top-3 h-7 w-7 rounded-full flex items-center justify-center ${a.color} ring-4 ring-background`}>
                            <Icon className="h-3.5 w-3.5" />
                          </div>
                          <div className="relative bg-card border rounded-xl pl-4 pr-4 py-3 overflow-hidden transition-all hover:shadow-md hover:-translate-y-px">
                            <span className={`absolute left-0 top-0 bottom-0 w-1 ${a.stripe}`} />
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                              <div className="min-w-0 flex-1 flex items-center gap-2 flex-wrap">
                                <span className="inline-flex items-center rounded-full bg-primary/10 text-primary text-[10px] font-bold px-2 py-0.5 uppercase tracking-wider font-head">{l.entity_type_label}</span>
                                <span className="font-head font-bold text-sm">{a.label}</span>
                                <span className="text-sm font-semibold truncate">· {l.entity_name || "—"}</span>
                              </div>
                              <span className="font-mono text-xs text-muted-foreground whitespace-nowrap tabular-nums" title={fullTime(l.created_at)}>{timeOf(l.created_at)}</span>
                            </div>
                            <div className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-semibold"><span className="h-5 w-5 rounded-full bg-primary/10 text-primary text-[9px] font-black flex items-center justify-center">{initials(l.user_name)}</span>{l.user_name}</div>
                            {l.action === "update" && (l.changes || []).length > 0 && (
                              <div className="mt-2 grid gap-1 sm:grid-cols-2">
                                {l.changes.map((c, i) => (
                                  <div key={i} className="text-xs flex items-center gap-1.5 flex-wrap bg-muted/40 rounded-lg px-2.5 py-1.5 min-w-0">
                                    <span className="font-semibold text-foreground">{c.label}:</span>
                                    <span className="text-muted-foreground line-through truncate max-w-[10rem]" title={auditVal(c.old)}>{auditVal(c.old)}</span>
                                    <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />
                                    <span className="text-foreground font-semibold truncate max-w-[10rem]" title={auditVal(c.new)}>{auditVal(c.new)}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
